import { useCallback, useEffect, useReducer, useRef } from "react";

interface AdvancedFetchState<T> {
  data?: T;
  error?: Error;
  isLoading: boolean;
  refetch: (options?: RequestInit) => void;
}

type Cache<T> = { [url: string]: T };

// discriminated union type
type Action<T> =
  | { type: "loading" }
  | { type: "fetched"; payload: T }
  | { type: "error"; payload: Error };

type ReducerState<T = unknown> = Omit<AdvancedFetchState<T>, "refetch">;

function useFetchAdvanced<T = unknown>(
  url?: string,
  options?: RequestInit,
  disableCache?: boolean
): AdvancedFetchState<T> {
  const cache = useRef<Cache<T>>({});

  // Used to prevent state update if the component is unmounted
  const cancelRequest = useRef<boolean>(false);

  const initialState: ReducerState<T> = {
    error: undefined,
    data: undefined,
    isLoading: false,
  };

  // Keep state logic separated
  const fetchReducer = (
    state: ReducerState<T>,
    action: Action<T>
  ): ReducerState<T> => {
    switch (action.type) {
      case "loading":
        return { ...state, isLoading: true };
      case "fetched":
        return { ...initialState, data: action.payload, isLoading: false };
      case "error":
        return { ...initialState, error: action.payload, isLoading: false };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(fetchReducer, initialState);

  const fetchData = useCallback(
    async (opts?: RequestInit) => {
      if (!url) return;

      dispatch({ type: "loading" });

      if (!disableCache) {
        // If a cache exists for this url, return it
        if (cache.current[url]) {
          dispatch({ type: "fetched", payload: cache.current[url] });
          return;
        }
      }

      try {
        const response = await fetch(url, opts ?? options);
        if (!response.ok) {
          // noinspection ExceptionCaughtLocallyJS
          throw new Error(response.statusText);
        }

        const data = (await response.json()) as T;
        if (!disableCache) cache.current[url] = data;
        if (cancelRequest.current) return;

        dispatch({ type: "fetched", payload: data });
      } catch (error) {
        if (cancelRequest.current) return;

        dispatch({ type: "error", payload: error as Error });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [url]
  );

  const refetch = useCallback(
    (options?: RequestInit) => {
      void fetchData(options);
    },
    [fetchData]
  );

  useEffect(() => {
    // Do nothing if the url is not given
    if (!url) return;

    cancelRequest.current = false;

    void fetchData();

    // Use the cleanup function for avoiding a possibly...
    // ...state update after the component was unmounted
    return () => {
      cancelRequest.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return { ...state, refetch };
}

export default useFetchAdvanced;
